/**
 * Iter270.1 / Task 8 — WelcomeTour
 *
 * Once-per-session overlay that introduces first-time visitors to
 * QorTrace's 3-step value prop on /. Dismissable via X, "Skip", or
 * by completing the tour. SessionStorage flag prevents re-show in the
 * same tab. Keyboard accessible (ESC + tab-trap), aria-friendly.
 *
 * Visibility rules:
 *  - Hidden once the user has set `qt:tour:dismissed:v1` in sessionStorage.
 *  - Hidden when window width < 720 (dense overlay doesn't fit).
 *  - Hidden if `?notour=1` is present (debugging escape hatch).
 *  - Hidden if `?demo=1` is present (Iter276.5 — clean URL for live
 *    demos / stage screenshots; doesn't persist, so real visitors who
 *    happen to type that query param still see the tour next visit).
 *  - Hidden when the user has an `qt_user_token` cookie (signed-in
 *    customers don't need the intro).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ArrowRight, ChevronLeft, Sparkles } from "lucide-react";

const SS_KEY = "qt:tour:dismissed:v1";

const STEPS = [
  {
    title: "Scan a wallet, free.",
    body:  "Drop any wallet or contract address. We score it against 18 chains in under 30 seconds. No card required.",
    cta:   "Next",
    accent: "#67E8F9",
  },
  {
    title: "See your full posture.",
    body:  "QorTrace turns the score into a signed report — chain coverage, exposure breakdown, fix recommendations. Public verify URL on every audit.",
    cta:   "Next",
    accent: "#67E8F9",
  },
  {
    title: "Monitor it continuously.",
    body:  "Wire Atlas to your treasury or multisig and get alerted the moment exposure shifts. Same engine, always on.",
    cta:   "Start a free scan",
    accent: "#67E8F9",
    link:  "/free-scan",
  },
];

function hasUserToken() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith("qt_user_token="));
}

export default function WelcomeTour() {
  const [open, setOpen]   = useState(false);
  const [stepI, setStepI] = useState(0);
  const dialogRef         = useRef(null);
  const lastFocusedRef    = useRef(null);

  // Decide whether to open on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("notour") === "1") return;
      // Iter276.5 — clean-URL escape hatch for live demos. Same behaviour
      // as `?notour=1` but more discoverable in talk notes & docs.
      if (params.get("demo") === "1") return;
      if (window.innerWidth < 720) return;
      if (sessionStorage.getItem(SS_KEY) === "1") return;
      if (hasUserToken()) return;
    } catch {
      return;
    }
    // Delay open by 1.2s so we don't fight the hero entrance animation
    const t = setTimeout(() => {
      lastFocusedRef.current = document.activeElement;
      setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback((reason) => {
    setOpen(false);
    try { sessionStorage.setItem(SS_KEY, "1"); } catch { /* no-op */ }
    if (lastFocusedRef.current && lastFocusedRef.current.focus) {
      try { lastFocusedRef.current.focus(); } catch { /* no-op */ }
    }
    if (typeof window !== "undefined" && window.posthog?.capture) {
      try { window.posthog.capture("welcome_tour_closed", { reason, step: stepI + 1 }); }
      catch { /* no-op */ }
    }
  }, [stepI]);

  // Focus + ESC + tab-trap
  useEffect(() => {
    if (!open) return;
    const root = dialogRef.current;
    if (!root) return;
    const firstFocusable = root.querySelector('[data-testid="welcome-tour-next-btn"]');
    if (firstFocusable) firstFocusable.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss("escape_key");
        return;
      }
      if (e.key === "Tab") {
        const focusables = root.querySelectorAll(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  const step    = STEPS[stepI];
  const isLast  = stepI === STEPS.length - 1;

  const next = () => {
    if (isLast) {
      // Tour completed — close, and if the step has a link, navigate.
      if (step.link) window.location.assign(step.link);
      dismiss("completed");
    } else {
      setStepI((i) => i + 1);
    }
  };

  return (
    <>
      <div
        data-testid="welcome-tour-backdrop"
        aria-hidden="true"
        // Iter273.1 (post-test finding) — Visual-only backdrop. We no
        // longer attach onClick because pointer-events is `none`: the
        // tour shouldn't gate the rest of the page (testers reported
        // the nav hovers feeling "frozen" on first visit). Dismissal
        // is via the X / Skip / Next / ESC controls inside the
        // dialog, which keeps pointer-events: auto via its own block.
        style={{
          position: "fixed", inset: 0,
          background: "rgba(2, 6, 18, 0.74)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          pointerEvents: "none",
          zIndex: 9000,
        }}
      />
      <div
        ref={dialogRef}
        data-testid="welcome-tour-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-tour-title"
        aria-describedby="welcome-tour-body"
        style={{
          position: "fixed",
          top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(520px, calc(100vw - 32px))",
          background: "linear-gradient(180deg, #0A1228 0%, #06091B 100%)",
          border: "1px solid #1f2a48", borderRadius: 14,
          padding: 28, color: "#E5EDF7",
          boxShadow: "0 22px 60px rgba(0,0,0,.5)",
          zIndex: 9001,
        }}
      >
        <button
          type="button"
          data-testid="welcome-tour-close-btn"
          aria-label="Close welcome tour"
          onClick={() => dismiss("close_button")}
          style={{
            position: "absolute", top: 10, right: 10,
            background: "transparent", border: "none",
            color: "#A5B5CE", cursor: "pointer", padding: 8,
            borderRadius: 6,
          }}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 8,
                      color: step.accent, fontFamily: "monospace",
                      fontSize: 11, letterSpacing: 1.5, marginBottom: 8 }}>
          <Sparkles size={14} aria-hidden="true" />
          WELCOME · STEP {stepI + 1}/{STEPS.length}
        </div>
        <h2
          id="welcome-tour-title"
          data-testid="welcome-tour-title"
          style={{ margin: "0 0 10px 0", fontSize: 24, lineHeight: 1.25 }}
        >
          {step.title}
        </h2>
        <p
          id="welcome-tour-body"
          data-testid="welcome-tour-body"
          style={{ margin: 0, color: "#A5B5CE", fontSize: 15, lineHeight: 1.55 }}
        >
          {step.body}
        </p>

        {/* Dots */}
        <div
          aria-hidden="true"
          style={{ display: "flex", gap: 6, justifyContent: "center",
                   margin: "22px 0 18px 0" }}
        >
          {STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                width: 26, height: 4, borderRadius: 2,
                background: i === stepI ? step.accent : "#1f2a48",
                transition: "background 220ms ease",
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      justifyContent: "space-between" }}>
          {stepI > 0 ? (
            <button
              type="button"
              data-testid="welcome-tour-back-btn"
              onClick={() => setStepI((i) => i - 1)}
              aria-label="Previous step"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "transparent", border: "1px solid #1f2a48",
                color: "#E5EDF7", padding: "8px 14px",
                borderRadius: 8, fontSize: 13, cursor: "pointer",
              }}
            >
              <ChevronLeft size={14} aria-hidden="true" /> Back
            </button>
          ) : (
            <button
              type="button"
              data-testid="welcome-tour-skip-btn"
              onClick={() => dismiss("skip_button")}
              aria-label="Skip welcome tour"
              style={{
                background: "transparent", border: "none",
                color: "#A5B5CE", padding: "8px 4px",
                fontSize: 13, cursor: "pointer",
              }}
            >
              Skip
            </button>
          )}
          <button
            type="button"
            data-testid="welcome-tour-next-btn"
            onClick={next}
            aria-label={isLast ? "Start a free scan" : "Next step"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: step.accent, border: "none", color: "#04091A",
              padding: "9px 18px", borderRadius: 8,
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            {step.cta} <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}
